/**
 * Parses the time portion out of an ISO datetime string like "2026-06-19T07:10:00".
 * Returns { hour: 0, min: 0 } on no match.
 *
 * @param {string} isoStr
 * @returns {{ hour: number, min: number }}
 */
export function parseISOTime(isoStr) {
  const match = isoStr.match(/T(\d{2}):(\d{2})/);
  if (!match) return { hour: 0, min: 0 };
  return { hour: parseInt(match[1], 10), min: parseInt(match[2], 10) };
}

/**
 * Formats an hour (0–23) and minute (0–59) into a 12-hour AM/PM string.
 *
 * @param {number} hour
 * @param {number} min
 * @returns {string} e.g. "7:10 AM"
 */
export function formatHourMin(hour, min) {
  const h = hour % 12 === 0 ? 12 : hour % 12;
  const m = String(min).padStart(2, '0');
  const ampm = hour < 12 ? 'AM' : 'PM';
  return `${h}:${m} ${ampm}`;
}

/**
 * Formats just the date portion of an ISO string.
 *
 * @param {string} isoStr
 * @returns {string} e.g. "Jun 19, 2026"
 */
/**
 * Formats a datetime ISO string (with timezone offset) as "Jun 23, 2026 · 6:17 AM".
 *
 * @param {string} isoStr
 * @returns {string}
 */
export function formatDateTime(isoStr) {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return isoStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' · '
    + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

export function formatISODate(isoStr) {
  if (!isoStr) return '—';
  const match = isoStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return isoStr;
  const date = new Date(`${match[1]}-${match[2]}-${match[3]}T00:00:00`);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Returns estimated crossover arrival info for an 18-hole player, or null.
 *
 * @param {number|null} holesPlayed - player's holes_to_play value
 * @param {number|null} teeTimeHole - 1 or 10
 * @param {number|null} teeTimeStartMins - tee time start as minutes from midnight
 * @param {number} nineHolePace - minutes to complete 9 holes (from nine_hole_pace setting)
 * @returns {{ time: string, destHole: number } | null}
 */
export function computeEstArrival(holesPlayed, teeTimeHole, teeTimeStartMins, nineHolePace) {
  if (teeTimeHole !== 1 && teeTimeHole !== 10) return null;
  if (holesPlayed !== 18) return null;
  if (teeTimeStartMins === null || teeTimeStartMins === undefined) return null;

  const totalMins = teeTimeStartMins + nineHolePace;
  const h = Math.floor(totalMins / 60) % 24;
  const m = totalMins % 60;
  const hh = h % 12 === 0 ? 12 : h % 12;
  const mm = String(m).padStart(2, '0');
  const ampm = h < 12 ? 'AM' : 'PM';

  return { time: `${hh}:${mm} ${ampm}`, destHole: teeTimeHole === 1 ? 10 : 1 };
}
